import { UserRole, UserPermissions, AdminPermissions } from '@/types';

// ==============================================
// DEFAULT ADMIN PERMISSIONS
// ==============================================

/**
 * Default permissions for platform admins
 */
export const DEFAULT_ADMIN_PERMISSIONS: AdminPermissions = {
  canViewAllFirms: true,
  canManageUsers: true,
  canManageBilling: true,
  canViewAuditLogs: true,
  canBrowseData: true,
  canImpersonate: true,
  canManageSettings: true,
};

// ==============================================
// ADMIN PERMISSION CHECKS
// ==============================================

/**
 * Check if admin has a specific permission
 */
export function hasAdminPermission(
  adminPermissions: AdminPermissions | null,
  permission: keyof AdminPermissions
): boolean {
  if (!adminPermissions) return false;
  return adminPermissions[permission] === true;
}

/**
 * Check if user is a platform admin (based on permissions object)
 */
export function isPlatformAdmin(adminPermissions: AdminPermissions | null): boolean {
  return adminPermissions !== null;
}

/**
 * Check if admin can view all firms
 */
export function canViewAllFirms(adminPermissions: AdminPermissions | null): boolean {
  return hasAdminPermission(adminPermissions, 'canViewAllFirms');
}

/**
 * Check if admin can manage users across all firms
 */
export function canAdminManageUsers(adminPermissions: AdminPermissions | null): boolean {
  return hasAdminPermission(adminPermissions, 'canManageUsers');
}

/**
 * Check if admin can manage billing
 */
export function canAdminManageBilling(adminPermissions: AdminPermissions | null): boolean {
  return hasAdminPermission(adminPermissions, 'canManageBilling');
}

/**
 * Check if admin can view audit logs
 */
export function canAdminViewAuditLogs(adminPermissions: AdminPermissions | null): boolean {
  return hasAdminPermission(adminPermissions, 'canViewAuditLogs');
}

/**
 * Check if admin can browse data
 */
export function canAdminBrowseData(adminPermissions: AdminPermissions | null): boolean {
  return hasAdminPermission(adminPermissions, 'canBrowseData');
}

/**
 * Check if admin can impersonate users/firms
 */
export function canAdminImpersonate(adminPermissions: AdminPermissions | null): boolean {
  return hasAdminPermission(adminPermissions, 'canImpersonate');
}

/**
 * Check if admin can manage system settings
 */
export function canAdminManageSettings(adminPermissions: AdminPermissions | null): boolean {
  return hasAdminPermission(adminPermissions, 'canManageSettings');
}

// ==============================================
// USER ROLE PERMISSIONS
// ==============================================

/**
 * Get permissions for a user role.
 *
 * Most staff actions are available to every staff role in v1 — roles are kept
 * for display, badges, and email-recipient targeting. Two exceptions keep this
 * map HONEST with how access is actually gated elsewhere (direct
 * `role === 'attorney'` checks + Firestore rules), so a future
 * `hasPermission(...)` gate can't silently grant all staff:
 *   - Provider edit/delete are platform-admin-only (global data integrity).
 *   - User + billing management are attorney-only (B16-2).
 */
export function getPermissionsForRole(role: UserRole): UserPermissions {
  const isAttorneyRole = role === 'attorney';
  return {
    canAddEditClients: true,
    canAddProviders: true,
    canEditProviders: false,      // Platform admin only
    canDeleteProviders: false,    // Platform admin only
    canApproveRequests: true,
    canLogReceivedRecords: true,
    canToggleRequests: true,
    canViewDashboard: true,
    canManageUsers: isAttorneyRole,    // attorney-only (matches real gating) — B16-2
    canManageBilling: isAttorneyRole,  // attorney-only (matches real gating) — B16-2
  };
}

/**
 * Check if user has specific permission
 */
export function hasPermission(
  role: UserRole,
  permission: keyof UserPermissions
): boolean {
  const permissions = getPermissionsForRole(role);
  return permissions[permission];
}

/**
 * Get user role display name
 */
export function getRoleDisplayName(role: UserRole): string {
  const displayNames: Record<UserRole, string> = {
    legal_assistant: 'Legal Assistant',
    paralegal: 'Paralegal',
    attorney: 'Attorney',
  };

  return displayNames[role];
}

/**
 * Get role-based badge colors for consistent styling across the app
 * Returns Tailwind CSS classes for background, text, and border colors
 */
export function getRoleBadgeColors(role: UserRole): string {
  const colorClasses: Record<UserRole, string> = {
    attorney: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400 border-purple-200 dark:border-purple-800',
    paralegal: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    legal_assistant: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-400 border-cyan-200 dark:border-cyan-800',
  };

  return colorClasses[role] || 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-400';
}

/**
 * Get all available roles
 */
export function getAllRoles(): UserRole[] {
  return ['legal_assistant', 'paralegal', 'attorney'];
}

/**
 * Check if user is attorney (admin)
 */
export function isAttorney(role: UserRole): boolean {
  return role === 'attorney';
}

/**
 * Check if user is attorney or paralegal
 */
export function isAttorneyOrParalegal(role: UserRole): boolean {
  return role === 'attorney' || role === 'paralegal';
}

/**
 * Check if user is any staff member
 */
export function isStaff(role: UserRole): boolean {
  return ['attorney', 'paralegal', 'legal_assistant'].includes(role);
}

/**
 * Check if user can manage documents (delete, edit metadata).
 * All staff can manage documents in v1.
 */
export function canManageDocuments(_role: UserRole): boolean {
  return true;
}

/**
 * Check if user can add new providers to the global database
 * All staff can add providers
 */
export function canAddProviders(role: UserRole): boolean {
  return hasPermission(role, 'canAddProviders');
}

/**
 * Check if user can edit provider records
 * Only platform admins can edit global provider records
 * Regular users (even attorneys) cannot edit providers to protect data integrity
 */
export function canEditProviders(role: UserRole): boolean {
  return hasPermission(role, 'canEditProviders');
}

/**
 * Check if user can delete provider records
 * Only platform admins can delete global provider records
 */
export function canDeleteProviders(role: UserRole): boolean {
  return hasPermission(role, 'canDeleteProviders');
}

/**
 * Check if user can view audit logs. All staff can view in v1.
 */
export function canViewAuditLogs(_user: { role: UserRole }): boolean {
  return true;
}

/**
 * Check if user can view billing information. All staff can view in v1.
 */
export function canViewBilling(_user: { role: UserRole }): boolean {
  return true;
}
